import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, Share2, Link2, Leaf } from "lucide-react";
import { useLang, fmtPrice, fmtMoney, SHIPPING } from "@/i18n";
import { Pic } from "@/components/Pic";
import { urlSlug } from "@/seo.mjs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://pame3d.com";

export const shareProduct = async ({ title, text, url, done }) => {
  if (navigator.share) {
    try { await navigator.share({ title, text, url }); return; } catch (e) { if (e?.name === "AbortError") return; }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener");
  done && done();
};

export const GameOffer = ({ slug, playPath }) => {
  const { t, lang, currency } = useLang();
  const o = t.offer;
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(`${API}/products/${slug}`).then((r) => setP(r.data)).catch(() => {}); }, [slug]);
  if (!p) return null;
  const name = lang === "es" ? p.name_es : p.name_en;
  const url = `${SITE}${playPath}`;
  const text = o.shareText.replace("{name}", name).replace("{price}", fmtPrice(p, currency));
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success(o.copied); } catch { toast.error(o.copyError); } };

  return (
    <section data-testid="game-offer" className="bg-white rounded-2xl border border-black/5 p-4 md:p-5 flex flex-col sm:flex-row gap-4 md:gap-6 items-center mb-8">
      <div className="w-full sm:w-40 md:w-48 shrink-0 aspect-square rounded-xl bg-[#EFECE6] overflow-hidden">
        <Pic src={p.image} alt={`${name} impreso en 3D`} className="w-full h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C86A4B] font-bold inline-flex items-center gap-1.5"><Leaf size={12} /> {o.kicker}</p>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mt-1">
          <h2 className="font-serif-d text-2xl md:text-3xl font-semibold text-[#132A1D]" data-testid="offer-name">{name}</h2>
          <p className="font-serif-d text-2xl md:text-3xl font-semibold text-[#2E5A3B]" data-testid="offer-price">{fmtPrice(p, currency)}</p>
        </div>
        <p className="text-sm text-[#5C5C5C] mt-2 line-clamp-2">{lang === "es" ? p.desc_es : p.desc_en}</p>
        <p className="text-xs text-[#5C5C5C] mt-1">{t.catalog.shippingNote}: {fmtMoney(SHIPPING[currency], currency)} · {p.material}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to={`/catalogo/${urlSlug(p.slug)}`} data-testid="offer-buy" className="inline-flex items-center gap-2 rounded-full bg-[#C86A4B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#2E5A3B] transition-colors duration-300"><ShoppingBag size={15} /> {o.buy}</Link>
          <button data-testid="offer-share" onClick={() => shareProduct({ title: name, text, url, done: () => toast.success(o.shared) })}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1DA851] transition-colors duration-300"><Share2 size={15} /> {o.share}</button>
          <button data-testid="offer-copy" onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-4 py-2.5 text-sm font-semibold hover:bg-[#EFECE6] transition-colors duration-300"><Link2 size={15} /> {o.copy}</button>
        </div>
      </div>
    </section>
  );
};
